import moment from "moment-timezone"
import { colourName, familyName, gameTypeName, genericName, joinNames, plural, quantityLabel, supplierNames, unitName } from "pages/EventsManagement/components/Tabs/Logistics/labels"
import { COLOUR_FAMILIES, RULES, TENIS10 } from "./productForm"

const sportNames = { tennis: "Tennis", padel: "Padel", tenis10: "Tenis10" }
export const sportName = sport => sportNames[sport] || sport
// A product of Tenis10 alone: it reaches only Tenis10 events, whatever their category (Events ADR 0016).
const isTenis10Product = ({ sports }) => sports?.length === 1 && sports[0] === TENIS10
// "Roșu", "every colour", "by hand only": the colours a Tenis10 product follows; a ball or lanyard without one never fits a line by itself.
const coloursLabel = ({ family, tenis10Levels }) => !COLOUR_FAMILIES.includes(family) ? "every colour"
  : tenis10Levels?.length ? tenis10Levels.map(colourName).join(", ") : "by hand only"

// "Balls · Tennis · All categories", "Balls · Tenis10 · Roșu": the family and the events a product applies to.
export const scopeLabel = (product, categoryNames = {}) => [familyName(product.family),
  product.sports ? product.sports.map(sportName).join(", ") : "All sports",
  isTenis10Product(product) ? coloursLabel(product)
    : product.categories ? product.categories.map(key => categoryNames[key] || key).join(", ") : "All categories"].join(" · ")

// "EN ✓ · BG — · covers 1 official ball": the translations a product has, and the balls it covers.
export const namesLabel = ({ names, officialBalls = [] }) => [`EN ${names?.en ? "✓" : "—"}`, `BG ${names?.bg ? "✓" : "—"}`,
  ...(officialBalls.length ? [`covers ${plural(officialBalls.length, "official ball")}`] : [])].join(" · ")

// "Places 1–2 · doubles × 2", "Medals Gold × 1.25", "1 bottle per player per match · Singles": a rule's parameters; a Tenis10 product's
// in Tenis10's words (singles only, children, competitions of its colour, groups on courts).
export const ruleParameters = (product, products = []) => {
  const { rule, unit, reserve } = product
  if (!rule) return null
  const tenis10 = isTenis10Product(product)
  const quantity = per => `${quantityLabel(rule.quantity, unit)} per ${per}${rule.gameType ? ` · ${gameTypeName(rule.gameType)}` : ""}`
  const last = rule.places ? Math.max(...rule.places) : null
  const parameters = {
    perNewBallMatch: null,
    perPlace: last && (tenis10 ? `Places 1–${last} of each competition · stock per place` : `${last > 1 ? `Places 1–${last}` : "Place 1"} · doubles × 2`),
    percentOf: `${products.find(({ id }) => id === rule.product)?.name || rule.product} × ${rule.factor}`,
    perParticipant: quantity(tenis10 ? "child of the Main List" : "participant"),
    perCompetition: quantity(tenis10 && product.tenis10Levels?.length ? "competition of its colour" : "competition"),
    fixed: `${quantityLabel(rule.quantity, unit)} per event`,
    perPlayerPerMatch: quantity("player per match"),
    perGroup: `${quantityLabel(rule.quantity, unit)} per group · one group per court`,
  }[rule.type]
  return [parameters, ...(reserve ? [`reserve ${reserve}%`] : [])].filter(Boolean).join(" · ") || null
}
// The Tenis10 medals' R2 counts every place, 1–4, each with its own stock (the canvas verdict C-A); an adult product keeps its words.
export const ruleName = (rule, product = {}) => !rule ? "No rule (manual quantity)"
  : rule.type === "perPlace" && isTenis10Product(product) ? "R2 · Per place" : RULES[rule.type] || rule.type

// "can · 4 balls": the unit on the sheet and what one unit holds.
export const unitLabel = ({ unit, perUnit, family }) => perUnit > 1 ? `${unitName(unit)} · ${plural(perUnit, family === "balls" ? "ball" : "piece")}`
  : unitName(unit)

export const supplierName = supplier => supplierNames[supplier] || supplier

// "Minimum 20" or "No minimum": the stock threshold of a product; every product is held in stock.
export const stockLabel = ({ minimum }) => minimum != null ? `Minimum ${minimum}` : "No minimum"
export const expiryLabel = expiresOn => `expires ${moment(expiresOn, "YYYY-MM-DD").format("D MMM YYYY")}`

// "5–6 Dec 2026", "31 Oct–1 Nov 2026": an event's dates in its timezone.
export const dateRange = (start, end, timezone = "Europe/Bucharest") => {
  if (!start) return "Dates not set"
  const [from, to] = [moment(start).tz(timezone), moment(end || start).tz(timezone)]
  if (from.isSame(to, "day")) return from.format("D MMM YYYY")
  if (from.isSame(to, "month")) return `${from.format("D")}–${to.format("D MMM YYYY")}`
  if (from.isSame(to, "year")) return `${from.format("D MMM")}–${to.format("D MMM YYYY")}`
  return `${from.format("D MMM YYYY")}–${to.format("D MMM YYYY")}`
}

// "the default supplier and rule": what an edit sets anew among the attributes that can reach a sheet.
const fieldNames = { family: "family", sports: "sports", categories: "categories", officialBalls: "covered official balls", tenis10Levels: "colours",
  unit: "unit", perUnit: "pieces per unit", supplier: "default supplier", rule: "rule", reserve: "reserve", active: "status" }
const comparable = value => JSON.stringify(Array.isArray(value) ? [...value].sort() : value ?? null)
export const changedFields = (stored, product) => joinNames(Object.keys(fieldNames)
  .filter(field => comparable(stored[field]) !== comparable(product[field])).map(field => fieldNames[field]))

// "Balls: MPG → Club", "Diplomas: kept · quantity set by admin": what a catalogue change does to one line.
const productName = side => side.product?.name || "no catalogue product"
export const impactLabel = line => {
  const name = line.generic ? genericName(line) : (line.after.product || line.before.product)?.name
  const notes = [
    ...(line.changes.includes("product") ? [`${productName(line.before)} → ${productName(line.after)}`] : []),
    ...(line.changes.includes("supplier") ? [`${supplierName(line.before.supplier)} → ${supplierName(line.after.supplier)}`] : []),
    ...(line.changes.includes("calculation") ? ["recalculated"] : []),
    ...line.kept.map(kept => `kept · ${kept} set by admin`),
  ]
  return `${name}: ${notes.join(" · ")}`
}
